/**
 * Airport Data Service — provides airport search and detailed lookup
 * capabilities backed by NASR data via OGC API Features and static fallback.
 */
import type { ResolvedWaypoint } from '@efb/shared-core';
import type { Feature } from 'geojson';
import { RestWaypointDataProvider } from '../adapters/RestWaypointDataProvider';
import {
  GPKG_COLLECTION_AIRPORTS,
  GPKG_COLLECTION_COMMUNICATIONS,
  GPKG_COLLECTION_RUNWAYS,
  buildOgcCollectionUrl,
} from '../config/environment';
import { getAuthHeader } from './AuthContext';
import type {
  AirportDetail,
  Runway,
  RunwayEnd,
  CommunicationFrequency,
  Services,
  OperatingInfo,
} from '../types/airport';

export interface AirportSearchResult {
  airports: ResolvedWaypoint[];
  totalResults: number;
  searchTime: number;
}

/** Properties exposed by the NASR APT_BASE feature collection */
interface NasrAirportProperties {
  arpt_id?: string;
  icao_id?: string;
  faa_ident?: string;
  arpt_name?: string;
  city?: string;
  state_code?: string;
  state_name?: string;
  county_name?: string;
  country_code?: string;
  elev?: number;
  mag_varn?: number;
  mag_hemis?: string;
  tpa?: number;
  resp_artcc_id?: string;
  fac_use?: string;
  owner_type_code?: string;
  twr_type_code?: string;
  facility_hours?: string;
  bcn_lens_color?: string;
  seg_circle_mkr_flag?: string;
  landing_fee_flag?: string;
  medical_use_flag?: string;
  notam_id?: string;
  airspace_class?: string;
  fuel_types?: string;
  oxygen_flag?: string;
  repair_services?: string;
  storage_services?: string;
  remarks?: string;
  effective_date?: string;
  expiration_date?: string;
  [key: string]: unknown;
}

interface NasrRunwayProperties {
  arpt_id?: string;
  rwy_id?: string;
  rwy_len?: number;
  rwy_width?: number;
  surface_type_code?: string;
  rwy_cond?: string;
  rwy_lgt_code?: string;
  cntrln_lgt_avbl?: string;
  // Base end
  base_end_id?: string;
  base_end_true_align?: number;
  base_end_mag_hdg?: number;
  base_end_lat?: number;
  base_end_lon?: number;
  base_end_elev?: number;
  base_end_tch?: number;
  base_end_displaced_thr?: number;
  base_end_vgsi?: string;
  base_end_ils_freq?: number;
  base_end_ils_ident?: string;
  base_end_app_light?: string;
  base_end_reil?: string;
  // Reciprocal end
  recip_end_id?: string;
  recip_end_true_align?: number;
  recip_end_mag_hdg?: number;
  recip_end_lat?: number;
  recip_end_lon?: number;
  recip_end_elev?: number;
  recip_end_tch?: number;
  recip_end_displaced_thr?: number;
  recip_end_vgsi?: string;
  recip_end_ils_freq?: number;
  recip_end_ils_ident?: string;
  recip_end_app_light?: string;
  recip_end_reil?: string;
  [key: string]: unknown;
}

interface NasrComProperties {
  arpt_id?: string;
  freq?: number;
  freq_use?: string;
  freq_name?: string;
  sector_facility?: string;
  [key: string]: unknown;
}

export class AirportDataService {
  private provider: RestWaypointDataProvider;

  constructor(provider?: RestWaypointDataProvider) {
    this.provider = provider ?? new RestWaypointDataProvider();
  }

  async search(query: string, limit = 20): Promise<AirportSearchResult> {
    const start = performance.now();
    const airports = await this.provider.searchByName(query, limit);
    return {
      airports,
      totalResults: airports.length,
      searchTime: performance.now() - start,
    };
  }

  async getAirport(identifier: string): Promise<ResolvedWaypoint | null> {
    return this.provider.resolveIdentifier(identifier);
  }

  async findNearby(lat: number, lng: number, radiusNm = 50, limit = 10): Promise<ResolvedWaypoint[]> {
    return this.provider.findNearest(lat, lng, radiusNm, limit);
  }

  /**
   * Fetch comprehensive airport detail from NASR via OGC API Features.
   * Falls back to basic data from the static provider when the backend
   * is unavailable.
   */
  async getAirportDetail(identifier: string): Promise<AirportDetail | null> {
    const id = identifier.trim().toUpperCase();

    // Try to fetch from OGC backend first
    try {
      const detail = await this.fetchDetailFromBackend(id);
      if (detail) return detail;
    } catch (err) {
      console.warn('AirportDataService: backend fetch failed, using fallback', err);
    }

    // Fallback to static provider
    return this.buildFallbackDetail(id);
  }

  // ─── Backend integration ───────────────────────────────────────────────

  private async fetchDetailFromBackend(icao: string): Promise<AirportDetail | null> {
    // Normalize FAA id (strip leading K for CONUS airports)
    const faaId = icao.length === 4 && icao.startsWith('K') ? icao.slice(1) : icao;

    // Fetch airport base record
    const airportUrl = buildOgcCollectionUrl(GPKG_COLLECTION_AIRPORTS);
    const airportRes = await fetch(
      `${airportUrl}/items?filter=${encodeURIComponent(
        `ident='${icao}' OR arpt_id='${faaId}'`,
      )}&filter-lang=cql2-text&limit=1&f=json`,
      { headers: getAuthHeader() },
    );
    if (!airportRes.ok) return null;
    const airportFc = await airportRes.json();
    const airportFeature: Feature | undefined = airportFc.features?.[0];
    if (!airportFeature) return null;

    const props = airportFeature.properties as NasrAirportProperties;
    const coords = (airportFeature.geometry as GeoJSON.Point)?.coordinates ?? [0, 0];

    // Fetch runways and communications in parallel
    const [runways, communications] = await Promise.all([
      this.fetchRunways(faaId),
      this.fetchCommunications(faaId),
    ]);

    return this.mapAirportDetail(props, coords, runways, communications);
  }

  private async fetchRunways(arptId: string): Promise<Runway[]> {
    try {
      const url = buildOgcCollectionUrl(GPKG_COLLECTION_RUNWAYS);
      const res = await fetch(
        `${url}/items?filter=${encodeURIComponent(`arpt_id='${arptId}'`)}&filter-lang=cql2-text&limit=20&f=json`,
        { headers: getAuthHeader() },
      );
      if (!res.ok) return [];
      const fc = await res.json();
      return (fc.features ?? []).map((f: Feature) =>
        this.mapRunway(f.properties as NasrRunwayProperties),
      );
    } catch {
      return [];
    }
  }

  private async fetchCommunications(arptId: string): Promise<CommunicationFrequency[]> {
    try {
      const url = buildOgcCollectionUrl(GPKG_COLLECTION_COMMUNICATIONS);
      const res = await fetch(
        `${url}/items?filter=${encodeURIComponent(`arpt_id='${arptId}'`)}&filter-lang=cql2-text&limit=50&f=json`,
        { headers: getAuthHeader() },
      );
      if (!res.ok) return [];
      const fc = await res.json();
      return (fc.features ?? [])
        .map((f: Feature) => this.mapCommunication(f.properties as NasrComProperties))
        .filter((c: CommunicationFrequency | null): c is CommunicationFrequency => c !== null);
    } catch {
      return [];
    }
  }

  // ─── Mappers ───────────────────────────────────────────────────────────

  private mapAirportDetail(
    props: NasrAirportProperties,
    coords: number[],
    runways: Runway[],
    communications: CommunicationFrequency[],
  ): AirportDetail {
    const magVarnSign = props.mag_hemis === 'W' ? -1 : 1;
    const magVarn = (props.mag_varn ?? 0) * magVarnSign;

    const operating: OperatingInfo = {
      use: props.fac_use ?? 'PU',
      ownership: props.owner_type_code ?? 'PU',
      towerType: props.twr_type_code,
      hours: props.facility_hours,
      beacon: props.bcn_lens_color,
      segmentedCircle: props.seg_circle_mkr_flag === 'Y',
      landingFee: props.landing_fee_flag === 'Y',
      medicalUse: props.medical_use_flag === 'Y',
    };

    const services: Services = {
      fuel: props.fuel_types ? props.fuel_types.split(/[,\s/]+/).filter(Boolean) : [],
      oxygen: props.oxygen_flag === 'Y',
      repair: Boolean(props.repair_services),
      storage: Boolean(props.storage_services),
      landingFee: props.landing_fee_flag === 'Y',
    };

    return {
      icao: (props.icao_id ?? props.arpt_id ?? '').toUpperCase(),
      faaId: (props.faa_ident ?? props.arpt_id ?? '').toUpperCase(),
      name: props.arpt_name ?? 'Unknown',
      city: props.city ?? '',
      state: props.state_code ?? props.state_name ?? '',
      county: props.county_name,
      country: props.country_code ?? 'US',
      latitude: coords[1] ?? 0,
      longitude: coords[0] ?? 0,
      elevation: props.elev ?? 0,
      magneticVariation: magVarn,
      patternAltitude: props.tpa,
      airspaceClass: props.airspace_class,
      notamFacility: props.notam_id,
      operating,
      services,
      runways,
      communications,
      remarks: props.remarks,
      nasrCycle: props.effective_date
        ? {
            effectiveDate: props.effective_date,
            expirationDate: props.expiration_date ?? '',
          }
        : undefined,
    };
  }

  private mapRunway(props: NasrRunwayProperties): Runway {
    const parts = (props.rwy_id ?? '').split('/');
    const baseDesignator = props.base_end_id ?? parts[0] ?? '';
    const recipDesignator = props.recip_end_id ?? parts[1] ?? '';

    const baseEnd: RunwayEnd = {
      designator: baseDesignator,
      trueHeading: props.base_end_true_align ?? 0,
      magneticHeading: props.base_end_mag_hdg ?? 0,
      latitude: props.base_end_lat ?? 0,
      longitude: props.base_end_lon ?? 0,
      elevation: props.base_end_elev ?? 0,
      thresholdCrossingHeight: props.base_end_tch,
      displacedThreshold: props.base_end_displaced_thr,
      vgsi: props.base_end_vgsi,
      ilsFrequency: props.base_end_ils_freq,
      ilsIdentifier: props.base_end_ils_ident,
      approachLighting: props.base_end_app_light,
      reil: props.base_end_reil === 'Y',
    };

    const reciprocalEnd: RunwayEnd = {
      designator: recipDesignator,
      trueHeading: props.recip_end_true_align ?? 0,
      magneticHeading: props.recip_end_mag_hdg ?? 0,
      latitude: props.recip_end_lat ?? 0,
      longitude: props.recip_end_lon ?? 0,
      elevation: props.recip_end_elev ?? 0,
      thresholdCrossingHeight: props.recip_end_tch,
      displacedThreshold: props.recip_end_displaced_thr,
      vgsi: props.recip_end_vgsi,
      ilsFrequency: props.recip_end_ils_freq,
      ilsIdentifier: props.recip_end_ils_ident,
      approachLighting: props.recip_end_app_light,
      reil: props.recip_end_reil === 'Y',
    };

    return {
      identifier: props.rwy_id ?? `${baseDesignator}-${recipDesignator}`,
      length: props.rwy_len ?? 0,
      width: props.rwy_width ?? 0,
      surface: props.surface_type_code ?? 'UNKN',
      condition: props.rwy_cond,
      edgeLighting: props.rwy_lgt_code,
      centerlineLighting: props.cntrln_lgt_avbl === 'Y',
      baseEnd,
      reciprocalEnd,
    };
  }

  private mapCommunication(props: NasrComProperties): CommunicationFrequency | null {
    if (!props.freq) return null;
    return {
      type: props.freq_use ?? 'UNKN',
      frequency: props.freq,
      name: props.freq_name,
      sectorFacility: props.sector_facility,
    };
  }

  // ─── Fallback ──────────────────────────────────────────────────────────

  private async buildFallbackDetail(icao: string): Promise<AirportDetail | null> {
    const wp = await this.provider.resolveIdentifier(icao);
    if (!wp) return null;

    const faaId = icao.length === 4 && icao.startsWith('K') ? icao.slice(1) : icao;

    return {
      icao: wp.identifier,
      faaId,
      name: wp.name,
      city: '',
      state: '',
      country: 'US',
      latitude: wp.latitude,
      longitude: wp.longitude,
      elevation: wp.elevation ?? 0,
      magneticVariation: 0,
      operating: {
        use: 'PU',
        ownership: 'PU',
      },
      services: {},
      runways: wp.longestRunwayFt
        ? [
            {
              identifier: 'UNKN',
              length: wp.longestRunwayFt,
              width: 0,
              surface: 'UNKN',
              baseEnd: {
                designator: '',
                trueHeading: 0,
                magneticHeading: 0,
                latitude: wp.latitude,
                longitude: wp.longitude,
                elevation: wp.elevation ?? 0,
              },
              reciprocalEnd: {
                designator: '',
                trueHeading: 180,
                magneticHeading: 180,
                latitude: wp.latitude,
                longitude: wp.longitude,
                elevation: wp.elevation ?? 0,
              },
            },
          ]
        : [],
      communications: [],
    };
  }
}
